import { format } from 'date-fns';
import pdfMake from 'pdfmake/build/pdfmake';
import { Content, ContentTable, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces';

import {
  NotebookMemoRecordFragment,
  PatientReportRecordFragment,
  PrimaryPharmacistRecordFragment,
} from '~/graphql/generated/graphql';
import { Label } from '~/utils/label';

import { ONLY_PERSON, toCreator } from './creator';
import { Patient, REPORT_TYPE } from './types';

type PatientDetailPdfInput = {
  patient?: Patient;
  patientReports: PatientReportRecordFragment[];
  memos: NotebookMemoRecordFragment[];
  primaryPharmacists: PrimaryPharmacistRecordFragment[];
  path: string;
};

const TABLE_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => '#cccccc',
  vLineColor: () => '#cccccc',
  paddingLeft: () => 8,
  paddingRight: () => 8,
  paddingTop: () => 5,
  paddingBottom: () => 5,
};

const LABEL_CELL_STYLE = { bold: true, fillColor: '#f5f5f5', noWrap: true };

const createPatientInfoTable = (patient?: Patient): ContentTable => {
  const rows: TableCell[][] = [];

  const addRow = (label: string, value: string | null | undefined) => {
    rows.push([{ text: label, ...LABEL_CELL_STYLE }, { text: value || '未登録' }]);
  };

  if (patient) {
    const nameText =
      patient.name && patient.kana
        ? `${patient.name}（${patient.kana}）`
        : patient.name || '未登録';
    rows.push([{ text: '氏名', ...LABEL_CELL_STYLE }, { text: nameText }]);

    const birthDateText = patient.birthDate
      ? `${Label.YYYYMMDDja(patient.birthDate)}（${Label.age(patient.birthDate)}）`
      : '未登録';
    rows.push([{ text: '生年月日', ...LABEL_CELL_STYLE }, { text: birthDateText }]);

    rows.push([{ text: '性別', ...LABEL_CELL_STYLE }, { text: patient.sex || '未登録' }]);

    const addressParts = [
      patient.postalCode ? `〒${patient.postalCode}` : null,
      patient.address,
    ].filter(Boolean);
    addRow('住所', addressParts.length > 0 ? addressParts.join(' ') : null);

    addRow('連絡先', patient.tel);
    addRow('緊急連絡先', patient.emergency);
    addRow('血液型', patient.bloodType);
    addRow('体重', patient.weight ? `${patient.weight}kg` : null);
  } else {
    rows.push([{ text: '患者情報', ...LABEL_CELL_STYLE }, { text: '未登録' }]);
  }

  return {
    table: {
      widths: ['auto', '*'],
      body: rows,
    },
    layout: TABLE_LAYOUT,
    margin: [0, 0, 0, 12],
  };
};

const createReportTable = (patientReports: PatientReportRecordFragment[]): ContentTable => {
  const allergies: string[] = [];
  const sideEffects: string[] = [];
  const medicalHistories: string[] = [];
  const others: string[] = [];

  for (const report of patientReports) {
    if (report.type === REPORT_TYPE.Allergy && report.content) allergies.push(report.content);
    if (report.type === REPORT_TYPE.SideEffect && report.content) sideEffects.push(report.content);
    if (report.type === REPORT_TYPE.MedicalHistory && report.content)
      medicalHistories.push(report.content);
    if (report.type === REPORT_TYPE.Other && report.content) others.push(report.content);
  }

  const toCell = (items: string[]): Content =>
    items.length > 0 ? { ul: items } : { text: 'なし' };

  return {
    table: {
      widths: ['auto', '*'],
      body: [
        [{ text: 'アレルギー歴', ...LABEL_CELL_STYLE }, toCell(allergies)],
        [{ text: '副作用歴', ...LABEL_CELL_STYLE }, toCell(sideEffects)],
        [{ text: '既往歴', ...LABEL_CELL_STYLE }, toCell(medicalHistories)],
        [{ text: 'その他', ...LABEL_CELL_STYLE }, toCell(others)],
      ],
    },
    layout: TABLE_LAYOUT,
    margin: [0, 0, 0, 12],
  };
};

const createMemosTable = (memos: NotebookMemoRecordFragment[]): ContentTable => {
  const memoItems: string[] = memos
    .filter((memo) => memo.content)
    .map((memo) => {
      const date = memo.enteredAt ? Label.YYYYMMDDja(memo.enteredAt) : '不明';
      const creator =
        memo.creator && ONLY_PERSON.includes(memo.creator) ? `｜${toCreator(memo.creator)}` : '';
      return `${memo.content}（${date}${creator}）`;
    });

  const cell: Content = memoItems.length > 0 ? { ul: memoItems } : { text: 'なし' };

  return {
    table: {
      widths: ['auto', '*'],
      body: [[{ text: 'お薬手帳メモ', ...LABEL_CELL_STYLE }, cell]],
    },
    layout: TABLE_LAYOUT,
    margin: [0, 0, 0, 12],
  };
};

const createPrimaryPharmacistsTable = (
  primaryPharmacists: PrimaryPharmacistRecordFragment[],
): ContentTable => {
  const rows: TableCell[][] = [];

  if (primaryPharmacists.length === 0) {
    rows.push([{ text: 'かかりつけ薬剤師', ...LABEL_CELL_STYLE }, { text: 'なし' }]);
  } else {
    primaryPharmacists.forEach((pharmacist, idx) => {
      const details: string[] = [];
      details.push(`氏名：${pharmacist.name || 'ー'}`);
      if (pharmacist.pharmacyName) details.push(`勤務先：${pharmacist.pharmacyName}`);
      if (pharmacist.contact) details.push(`連絡先：${pharmacist.contact}`);
      if (pharmacist.start || pharmacist.end) {
        const startDate = pharmacist.start ? Label.YYYYMMDDja(pharmacist.start) : '不明';
        const endPart = pharmacist.end ? `〜${Label.YYYYMMDDja(pharmacist.end)}` : '〜';
        details.push(`担当開始日：${startDate}${endPart}`);
      }

      const label =
        idx === 0
          ? { text: 'かかりつけ薬剤師', ...LABEL_CELL_STYLE }
          : { text: '', ...LABEL_CELL_STYLE };
      rows.push([label, { stack: details.map((d) => ({ text: d })) }]);
    });
  }

  return {
    table: {
      widths: ['auto', '*'],
      body: rows,
    },
    layout: TABLE_LAYOUT,
    margin: [0, 0, 0, 12],
  };
};

const buildDocumentDefinitions = (input: PatientDetailPdfInput): TDocumentDefinitions => ({
  content: [
    { text: 'お薬手帳かかりつけ情報', fontSize: 14, bold: true, margin: [0, 0, 0, 16] },
    createPatientInfoTable(input.patient),
    createReportTable(input.patientReports),
    createMemosTable(input.memos),
    createPrimaryPharmacistsTable(input.primaryPharmacists),
  ],
  pageSize: 'A4',
  pageOrientation: 'portrait',
  defaultStyle: {
    font: 'M_PLUS_1p',
    fontSize: 9,
  },
  footer: (currentPage, pageCount) => ({
    text: `${currentPage} / ${pageCount}`,
    alignment: 'center',
    fontSize: 8,
    margin: [0, 8, 0, 0],
  }),
});

export const buildFileName = (patient?: Patient): string => {
  const name = patient?.name ?? '患者不明';
  const date = format(new Date(), 'yyyyMMdd');
  return `お薬手帳かかりつけ情報_${name}_${date}`;
};

export const createPatientDetailPdf = (input: PatientDetailPdfInput) => {
  const fonts = {
    M_PLUS_1p: {
      normal: `${input.path}/fonts/M_PLUS_1p/MPLUS1p-Regular.ttf`,
      bold: `${input.path}/fonts/M_PLUS_1p/MPLUS1p-Bold.ttf`,
    },
  };
  return pdfMake.createPdf(buildDocumentDefinitions(input), {}, fonts);
};
